import { CellValueUnion, FieldCellValueUI, FormFieldValueGroupUI, FormFieldValuesUI, RowViewState } from "Backend";
import {
  Block,
  Cell,
  CellValue,
  FieldValueGroupReference,
  FieldValueGroupSelectionStatus,
  FieldValueGroupTag,
  Form,
  FormField,
} from "generated/graphql";
import { createContext, MouseEvent } from "react";
import { StateHandle } from "StateHandle";
import type { FieldUIState, ProfileGenRunInfo } from "../helpers/hooks/useCaseForm";
import { UsePopover } from "helpers/hooks/usePopover";

export interface CellWithAccs {
  cell: Cell;
  accs: CellAcc[];
}

export interface CellAcc {
  cellValue: CellValue;
  cell: Cell;
  count: number;
}

export interface GroupedValueGroup {
  originalValue: CellValueUnion | undefined;
  cellValue: string;
  valueGroups: FormFieldValueGroupUI[];
  sortedDates: Date[];
  cellAccs: CellWithAccs[];
}

export interface GroupedValueGroupByCellValue {
  [cellValue: string]: GroupedValueGroup;
}

export interface ValueGroupsByDate {
  date: Date;
  valueGroups: FormFieldValueGroupUI[];
}

export interface InitialCell {
  cellId: number;
  value: CellValue;
}

export interface FormFieldOperations {
  form: Form;
  formField: FormField;
  state: FormFieldValuesUI;
  valueGroups: FormFieldValueGroupUI[];
  localStarFilteredValueGroups: FormFieldValueGroupUI[];
  valueGroupValuesHandle: (index: number) => StateHandle<FieldCellValueUI[]> | void;
  loadHasFindings: () => Promise<void>;
  groupByCellIdHandle: [number, (cellId: number) => void];
  groupedValueGroupStartDateHandle: StateHandle<Date | undefined>;
  groupedValueGroupEndDateHandle: StateHandle<Date | undefined>;
  valueGroupValuesHandleForGroup: (groupId: number) => StateHandle<FieldCellValueUI[]> | undefined;
  valueGroupHandleForGroup: (groupId: number) => StateHandle<FormFieldValueGroupUI> | undefined;
  /** 👇 Queries about the state 👇 **/
  getNextValueGroup: (groupId: number) => FormFieldValueGroupUI | undefined;
  valueGroupById: (groupId: number) => FormFieldValueGroupUI | undefined;
  cellByIdMap: Map<number, Cell>;
  hasValueGroupsFromAI: boolean;
  canAddValueGroup: boolean;
  hasAnyDateCells: boolean;
  hasAnyDateCellsWithValue: boolean;
  validGroupByCells: Cell[];
  valueGroupsByDate: ValueGroupsByDate[];
  getValueGroupPosition: (groupId: number) => number | undefined;
  isValueGroupFirst: (groupId: number) => boolean;
  isValueGroupLast: (groupId: number) => boolean;
  fieldUIState: FieldUIState | undefined;
  groupByCell: Cell | undefined;
  hasFindings: boolean | undefined;
  profileGenRunSummary: ProfileGenRunInfo | undefined;
  dateCellIndex: number;
  lensSubtypeCellIndex: number;
  nameCellIndex: number;
  recordTypeCellIndex: number;
  /** 👇 Methods to mutate values groups and field state 👇 **/
  setGroupExpanded: (expanded: boolean) => void;
  expandGroupName: (groupName: string) => void;
  setFilterByStar: (filterValue: boolean) => void;
  setGroupSort: (cellValue: string, cell?: Cell, sortDir?: "asc" | "desc") => void;
  collapseGroupName: (groupName: string) => void;
  addValueGroup: (initialCell?: InitialCell) => void;
  setValueGroupNotes: (groupId?: number, notes?: string) => void;
  setValueGroupErrors: (groupId: number) => boolean;
  setValueGroupViewState: (groupId: number, viewState: RowViewState) => void;
  setValueGroupViewStateBulk: (groupIds: number[], viewState: RowViewState) => void;
  removeReference: (groupId: number, referenceId: number) => Promise<void>;
  updateReference: (body: {
    groupId: number;
    reference: FieldValueGroupReference;
    pageRangeStart?: number;
    pageRangeEnd?: number;
  }) => Promise<void>;
  addReferenceToValueGroup: (
    groupId: number,
    intakeId: number,
    intakePage: number,
    block: Block,
    pageRangeStart?: number,
    pageRangeEnd?: number
  ) => Promise<void>;
  removeValueGroup: (groupId: number, callback: () => void, popover?: UsePopover) => void;
  removeValueGroups: (groupIds: number[], callback: () => void, popover?: UsePopover, groupName?: string) => void;
  selectValueGroups: (groupIds: number[], action: FieldValueGroupSelectionStatus) => Promise<void>;
  swapValueGroups: (evt: MouseEvent, idx: number, dir: "up" | "down") => void;
  sortResponses: (cellId: number, order: "ascending" | "descending") => void;
  saveValueGroup: (groupId: number, returnToViewState?: RowViewState) => Promise<boolean | undefined>;
  saveYesNo: (yesNo: boolean | undefined) => void;
  setValueGroupTags: (groupId: number, tags: FieldValueGroupTag[]) => void;
  tagHandle: StateHandle<Set<string>>;
}

export const FormFieldContext = createContext<FormFieldOperations>(null!);
